import { Analytics } from '@analytics';
import { removeFromTeam } from '@api/taptotip/group';
import ConfirmIcon from '@assets/images/ConfirmIcon.svg';
import { GroupEntityType } from '@constants';
import { Button } from '@material-ui/core';
import { useInjectSaga } from '@utils/injectSaga';
import { isMerchantOfRecordCountry } from '@utils/helpers';
import React, { useEffect } from 'react';
import { useIntl } from 'react-intl';
import { connect } from 'react-redux';
import * as actions from '../../../../containers/TippingGroupPage/actions';
import saga from '../../../../containers/TippingGroupPage/saga';
import GroupDetailsCard from '../GroupDetailsCard';
import messages from './messages';

// Tailwind class strings for the MUI buttons (important modifier used to win over MUI defaults)
const actionButtonBase =
  'w-full max-w-[354px] h-[39px] !rounded-xl !font-semibold !shadow-sm !normal-case mt-6 first-of-type:mt-3 disabled:!bg-slate-300 disabled:!text-white';
const primaryButtonClasses =
  '!bg-indigo-600 hover:!bg-indigo-700 !text-white !font-semibold !px-5 !py-2.5 !rounded-xl !shadow-sm !normal-case';
const successButtonClasses = '!bg-emerald-600 hover:!bg-emerald-700 !text-white !normal-case';
const dialogDangerButtonClasses =
  '!rounded-xl !font-semibold !normal-case !bg-red-600 hover:!bg-red-700 !text-white';
const dialogSuccessButtonClasses =
  '!rounded-xl !font-semibold !normal-case !bg-emerald-600 hover:!bg-emerald-700 !text-white';
const cancelButtonClasses = 'mt-2 !text-slate-400 !rounded-xl !normal-case';
const dialogIconClasses = 'flex w-full h-full justify-center items-center';
const dialogAvatarIconClasses = 'bg-white rounded-full shadow-md';

const GroupDetails = ({
  group,
  user,
  resource,
  cardOwnerElement,
  cardOwnerType,
  isUserInGroup,
  setDescription,
  setButtons,
  setDialogIcon,
  setIsConfirmationDialogShown,
  closeConfirmationDialog,
  fullSize = false,
  setCardView,
  addToGroup
}) => {
  const intl = useIntl();
  useInjectSaga({ key: 'tipPayments', saga });

  useEffect(() => {
    Analytics.trackViewFeature('Teams');
  }, []);

  const showRemoveFromTeamDialog = ({ groupId, type, id, description, icon }) => {
    setDialogIcon(icon);
    setDescription(description);

    setButtons([
      <Button
        key="remove-button"
        className={dialogDangerButtonClasses}
        onClick={() => {
          Analytics.track('remove from group', {
            type
          });
          removeFromTeam(groupId, type, id);
        }}>
        {intl.formatMessage(messages.removeFromTeam)}
      </Button>,
      <Button
        className={cancelButtonClasses}
        color="secondary"
        key="cancel-button"
        onClick={closeConfirmationDialog}>
        {intl.formatMessage(messages.cancel)}
      </Button>
    ]);

    setIsConfirmationDialogShown(true);
  };

  const showLeaveTeamDialog = ({ groupId, userId, icon }) => {
    setDialogIcon(icon);
    setDescription(intl.formatMessage(messages.leaveTeamConfirmation));

    setButtons([
      <Button
        key="remove-button"
        className={dialogSuccessButtonClasses}
        onClick={() => {
          Analytics.track('leave group');
          removeFromTeam(groupId, 'user', userId);
        }}>
        {intl.formatMessage(messages.leaveTeam)}
      </Button>,
      <Button
        className={cancelButtonClasses}
        color="secondary"
        key="cancel-button"
        onClick={closeConfirmationDialog}>
        {intl.formatMessage(messages.cancel)}
      </Button>
    ]);

    setIsConfirmationDialogShown(true);
  };

  const showClaimResourceDialog = ({ resourceId }) => {
    setDialogIcon(<img alt="confirm" src={ConfirmIcon} />);
    setDescription(intl.formatMessage(messages.claimResourceConfirmation));

    setButtons([
      <Button
        className={dialogSuccessButtonClasses}
        key="confirm-button"
        onClick={() => {
          Analytics.trackUseFeature('Teams');
          Analytics.track('add to team', {
            type: GroupEntityType.resource
          });
          addToGroup({
            type: GroupEntityType.resource,
            id: resourceId
          });
        }}>
        {intl.formatMessage(messages.claimResource)}
      </Button>,
      <Button
        className={cancelButtonClasses}
        color="secondary"
        key="cancel-button"
        onClick={closeConfirmationDialog}>
        {intl.formatMessage(messages.cancel)}
      </Button>
    ]);

    setIsConfirmationDialogShown(true);
  };

  const renderActionButtons = () => {
    const buttons = [];

    if (!group) {
      buttons.push(
        <Button
          key="add-member-button"
          className={`${actionButtonBase} ${primaryButtonClasses}`}
          onClick={() => {
            Analytics.trackUseFeature('Teams');
            Analytics.track('add to team');
            return addToGroup({
              type: GroupEntityType.user,
              id: cardOwnerElement._id
            });
          }}>
          {intl.formatMessage(messages.addToGroup)}
        </Button>);
    }

    // Merchant-of-record guides (US, Canada) cannot join a team.
    const isUS = isMerchantOfRecordCountry(user?.profile?.country);

    if (group && !isUserInGroup && !isUS) {
      buttons.push(
        <Button
          key="join-button"
          className={`${actionButtonBase} ${primaryButtonClasses}`}
          onClick={() => {
            Analytics.trackUseFeature('Teams');
            Analytics.track('join group');
            return addToGroup({
              groupId: group._id,
              type: GroupEntityType.user,
              id: user.userId
            });
          }}>
          {intl.formatMessage(messages.joinTeam)}
        </Button>
      );
    }

    if (!isUserInGroup && cardOwnerType === GroupEntityType.resource) {
      /*
      buttons.push(
        <Button
          key="claim-button"
          className={classes.button}
          onClick={() => {
            Analytics.trackUsedFeature('Teams');
            Analytics.track('claim resource', {
              type: GroupEntityType.resource
            });
            showClaimResourceDialog({
              resourceId: cardOwnerElement._id
            });
          }}>
          {intl.formatMessage(messages.claimResource)}
        </Button>
      );*/
    }

    if (isUserInGroup) {
      buttons.push(
        <Button
          key="leave-button"
          className={`${actionButtonBase} ${successButtonClasses}`}
          onClick={() =>
            showLeaveTeamDialog({
              icon: (
                <img
                  className={`${dialogIconClasses} ${dialogAvatarIconClasses}`}
                  src={user.profile.avatarUrl}
                  alt="users avatar"
                />
              ),
              groupId: group._id,
              userId: user.userId
            })
          }>
          {intl.formatMessage(messages.leaveTeam)}
        </Button>
      );
    }

    return buttons;
  };

  return (
    <>
      <div className="flex flex-col justify-start">
        <div>
          <div>
            <GroupDetailsCard
              isUserInGroup={isUserInGroup}
              group={group}
              type={GroupEntityType.user}
              user={user}
              elements={group.users}
              showRemoveFromTeamDialog={showRemoveFromTeamDialog}
              fullSize={fullSize}
              setCardView={setCardView}
            />
          </div>
          <div>
            <GroupDetailsCard
              isUserInGroup={isUserInGroup}
              group={group}
              type={GroupEntityType.resource}
              elements={group.resources}
              showRemoveFromTeamDialog={showRemoveFromTeamDialog}
              fullSize={fullSize}
            />
          </div>
          <div className="mt-2 flex flex-col justify-center items-center">{renderActionButtons()}</div>
        </div>
      </div>
    </>
  );
};

const mapStateToProps = () => ({
});

const mapDispatchToProps = dispatch => ({
  addToGroup: info => dispatch(actions.addToGroup(info))
});

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(GroupDetails);
